import styled from '@emotion/styled';
import type { AllDateRange } from '@hotjar/webapp/constants/DateFilterRanges';
import { DATE_RANGE_TYPE } from '@hotjar/webapp/constants/DateFilterRanges';
import { DateRangeFilter } from '@hotjar/global-filters';
import type { DateRange } from '@hotjar/global-filters';
import { useCurrentOrganization } from '@hotjar/webapp/hooks/useCurrentOrganization';
import { tokens } from '@hotjar/hotsauce';
import { AuthorFilter } from './AuthorFilter';
import { LabelFilter } from './LabelFilter';
import { SourceFilter } from './SourceFilter';
import type { FiltersState } from './use-filter-state';
import { SolvedStatusFilter } from './SolvedStatusFilter';

const FiltersContainer = styled.div`
  display: flex;
  gap: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x2};
`;

interface FiltersProps {
  siteId: React.ComponentProps<typeof LabelFilter>['siteId'];
  authors: FiltersState['authors'];
  dateRange: FiltersState['dateRange'];
  labels: FiltersState['labels'];
  sources: FiltersState['sources'];
  status: FiltersState['status'];
  onAuthorsChange: (users: FiltersState['authors']) => void;
  onDateRangeChange: (date: FiltersState['dateRange']) => void;
  onLabelsChange: (date: FiltersState['labels']) => void;
  onSourcesChange: (source: FiltersState['sources']) => void;
  onStatusChange: (source: FiltersState['status']) => void;
}

export const Filters = ({
  siteId,
  authors,
  dateRange,
  labels,
  sources,
  status,
  onAuthorsChange,
  onDateRangeChange,
  onLabelsChange,
  onSourcesChange,
  onStatusChange,
}: FiltersProps): JSX.Element => {
  const { data: organization } = useCurrentOrganization();

  const defaultDate: AllDateRange = {
    type: DATE_RANGE_TYPE.ALL,
  };

  return (
    <FiltersContainer>
      <DateRangeFilter
        currentValue={(dateRange ?? defaultDate) as DateRange}
        onApply={(val) => onDateRangeChange(val as DateRange)}
      />

      <AuthorFilter
        selectedUsers={authors}
        onApply={onAuthorsChange}
        users={organization?.users ?? []}
      />

      <SourceFilter onApply={onSourcesChange} sources={sources} />

      <LabelFilter onApply={onLabelsChange} selectedLabels={labels} siteId={siteId} />

      <SolvedStatusFilter onApply={onStatusChange} selected={status} />
    </FiltersContainer>
  );
};
